import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import {
  Search, Shield, CheckCircle, Eye,
  Clock, MapPin, Globe, RefreshCw,
  MessageSquare, AlertTriangle
} from 'lucide-react';

const STATUS_CONFIG = {
  active: { color: '#10B981', bg: '#D1FAE5', label: 'Active' },
  warned: { color: '#F59E0B', bg: '#FEF3C7', label: 'Warned' },
  temp_banned: { color: '#EF4444', bg: '#FFE4E6', label: 'Temp Banned' },
  permanent_banned: { color: '#7F1D1D', bg: '#FCA5A5', label: 'Perm Banned' },
};

const ROLE_CONFIG = {
  student: { color: '#4F8EF7', bg: 'rgba(79,142,247,0.1)', label: 'Student' },
  teacher: { color: '#7C3AED', bg: 'rgba(124,58,237,0.1)', label: 'Teacher' },
  admin: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', label: 'Admin' },
};

const APPEAL_CONFIG = {
  pending: { color: '#F59E0B', bg: '#FEF3C7', label: 'Pending' },
  approved: { color: '#10B981', bg: '#D1FAE5', label: 'Approved' },
  denied: { color: '#EF4444', bg: '#FFE4E6', label: 'Denied' },
};

export default function AdminUsers() {
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'appeals'
  const [users, setUsers] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionForm, setActionForm] = useState({
    type: 'warning', reason: '', days: 7,
  });
  const [processing, setProcessing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [appealResponse, setAppealResponse] = useState('');
  const [appealProcessing, setAppealProcessing] = useState(false);
  const [stats, setStats] = useState({
    total: 0, students: 0, teachers: 0, warned: 0, banned: 0,
  });

  useEffect(() => {
    loadUsers();
    loadAppeals();
  }, []);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // ── Load users ──────────────────────────────────────
  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(
          'id, full_name, email, role, institution_id, institution_name, ' +
          'created_at, last_seen_at, last_ip, last_location, last_country, ' +
          'account_status, ban_type, ban_reason, ban_until, warning_count, ' +
          'avatar_url, degree, username, delete_requested'
        )
        .neq('role', 'admin')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('loadUsers error:', error);
        setLoading(false);
        return;
      }

      const list = data || [];
      setUsers(list);
      setStats({
        total: list.length,
        students: list.filter(u => u.role === 'student').length,
        teachers: list.filter(u => u.role === 'teacher').length,
        warned: list.filter(u => u.account_status === 'warned').length,
        banned: list.filter(u =>
          ['temp_banned', 'permanent_banned'].includes(u.account_status)).length,
      });
    } catch (err) {
      console.error('loadUsers catch:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Load appeals ────────────────────────────────────
  const loadAppeals = async () => {
    try {
      const { data, error } = await supabase
        .from('user_appeals')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) { console.error('loadAppeals error:', error); return; }
      setAppeals(data || []);
    } catch (err) {
      console.error('loadAppeals catch:', err);
    }
  };

  // ── Issue action ────────────────────────────────────
  const issueAction = async () => {
    if (!selected || !actionForm.reason.trim()) return;
    setProcessing(true);
    try {
      const { data: { user: adminUser } } = await supabase.auth.getUser();
      const adminId = adminUser?.id;

      let updates = {};
      let banUntil = null;

      if (actionForm.type === 'warning') {
        updates = {
          account_status: 'warned',
          ban_reason: actionForm.reason,
          warning_count: (selected.warning_count || 0) + 1,
        };
      } else if (actionForm.type === 'temp_ban') {
        banUntil = new Date(
          Date.now() + actionForm.days * 24 * 60 * 60 * 1000
        ).toISOString();
        updates = {
          account_status: 'temp_banned',
          is_banned: true,
          ban_type: 'temp',
          ban_reason: actionForm.reason,
          ban_until: banUntil,
        };
      } else if (actionForm.type === 'permanent_ban') {
        updates = {
          account_status: 'permanent_banned',
          is_banned: true,
          ban_type: 'permanent',
          ban_reason: actionForm.reason,
          ban_until: null,
        };
      }

      // Update profile
      const { error: updateErr } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', selected.id);

      if (updateErr) {
        console.error('Profile update error:', updateErr);
        alert('Failed to update: ' + updateErr.message);
        setProcessing(false);
        return;
      }

      // Log in user_flags
      const { error: flagErr } = await supabase.from('user_flags').insert({
        user_id: selected.id,
        flagged_by: adminId,
        flag_type: actionForm.type === 'warning' ? 'warning'
          : actionForm.type === 'temp_ban' ? 'temp_ban' : 'permanent_ban',
        reason: actionForm.reason,
        ban_start_at: new Date().toISOString(),
        ban_end_at: banUntil,
        is_active: true,
      });

      if (flagErr) console.error('Flag insert error:', flagErr);

      setActionModal(null);
      setSelected(null);
      setActionForm({ type: 'warning', reason: '', days: 7 });
      showSuccess(`✅ ${actionForm.type === 'warning' ? 'Warning issued'
        : actionForm.type === 'temp_ban' ? `${actionForm.days}-day ban applied`
        : 'Permanent ban applied'} to ${selected.full_name}`);
      await loadUsers();
    } catch (err) {
      console.error('issueAction error:', err);
      alert('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ── Lift ban ────────────────────────────────────────
  const liftBan = async (userId, userName) => {
    setProcessing(true);
    try {
      const { error } = await supabase.from('profiles').update({
        account_status: 'active',
        is_banned: false,
        ban_type: null,
        ban_reason: null,
        ban_until: null,
      }).eq('id', userId);

      if (error) { console.error('liftBan error:', error); setProcessing(false); return; }

      await supabase.from('user_flags')
        .update({ is_active: false, resolved_at: new Date().toISOString() })
        .eq('user_id', userId).eq('is_active', true);

      setActionModal(null);
      setSelected(null);
      showSuccess(`✅ Ban lifted for ${userName}`);
      await loadUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  // ── Resolve appeal ──────────────────────────────────
  const resolveAppeal = async (appealId, userId, decision) => {
    setAppealProcessing(true);
    try {
      const { data: { user: adminUser } } = await supabase.auth.getUser();

      await supabase.from('user_appeals').update({
        status: decision,
        admin_response: appealResponse.trim() || null,
        resolved_at: new Date().toISOString(),
        resolved_by: adminUser?.id,
      }).eq('id', appealId);

      // If approved, lift the ban automatically
      if (decision === 'approved') {
        await supabase.from('profiles').update({
          account_status: 'active',
          is_banned: false,
          ban_type: null,
          ban_reason: null,
          ban_until: null,
        }).eq('id', userId);

        await supabase.from('user_flags')
          .update({ is_active: false, resolved_at: new Date().toISOString() })
          .eq('user_id', userId).eq('is_active', true);
      }

      setSelectedAppeal(null);
      setAppealResponse('');
      showSuccess(`✅ Appeal ${decision === 'approved' ? 'approved — ban lifted' : 'denied'}`);
      await loadAppeals();
      await loadUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setAppealProcessing(false);
    }
  };

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (statusFilter !== 'all' && u.account_status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        u.full_name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.institution_name?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const pendingAppeals = appeals.filter(a => a.status === 'pending').length;

  return (
    <AdminLayout>
      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>
          Users & Moderation
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>
          Manage students and teachers, issue warnings and bans
        </p>
      </div>

      {/* ── Success message ── */}
      {successMsg && (
        <div style={{
          background: '#F0FDF4', border: '1px solid #BBF7D0',
          borderRadius: 12, padding: '12px 16px', marginBottom: 20,
          fontSize: 13, fontWeight: 600, color: '#16A34A',
        }}>
          {successMsg}
        </div>
      )}

      {/* ── Stats ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5,1fr)',
        gap: 12, marginBottom: 24,
      }}>
        {[
          { label: 'Total Users', value: stats.total, color: '#4F8EF7', icon: '👥' },
          { label: 'Students', value: stats.students, color: '#10B981', icon: '🎓' },
          { label: 'Teachers', value: stats.teachers, color: '#7C3AED', icon: '📚' },
          { label: 'Warned', value: stats.warned, color: '#F59E0B', icon: '⚠️' },
          { label: 'Banned', value: stats.banned, color: '#EF4444', icon: '🚫' },
        ].map((s, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 14, padding: '16px',
            border: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 24 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>
                {s.value}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tab switcher ── */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 20,
        background: '#F1F5F9', borderRadius: 12, padding: 4,
        width: 'fit-content',
      }}>
        {[
          { key: 'users', label: 'Users' },
          { key: 'appeals', label: `Appeals${pendingAppeals > 0 ? ` (${pendingAppeals})` : ''}` },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none',
            background: activeTab === t.key ? '#fff' : 'transparent',
            color: activeTab === t.key ? '#1E293B' : '#64748B',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
            boxShadow: activeTab === t.key
              ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.2s',
          }}>
            {t.label}
            {t.key === 'appeals' && pendingAppeals > 0 && (
              <span style={{
                marginLeft: 4, background: '#EF4444', color: '#fff',
                borderRadius: '50%', width: 18, height: 18,
                display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 10, fontWeight: 800,
              }}>{pendingAppeals}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div style={{
            background: '#fff', borderRadius: 14,
            border: '1px solid #E2E8F0', padding: '16px 20px',
            marginBottom: 20,
            display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <Search size={15} style={{
                position: 'absolute', left: 11,
                top: '50%', transform: 'translateY(-50%)', color: '#94A3B8',
              }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search name, email, institution..."
                style={{
                  width: '100%', padding: '9px 12px 9px 34px',
                  border: '1.5px solid #E2E8F0', borderRadius: 10,
                  fontSize: 13, outline: 'none',
                }} />
            </div>
            {[
              { setter: setRoleFilter, state: roleFilter,
                opts: [['all','All Roles'],['student','Students'],['teacher','Teachers']] },
              { setter: setStatusFilter, state: statusFilter,
                opts: [['all','All Status'],['active','Active'],['warned','Warned'],
                  ['temp_banned','Temp Banned'],['permanent_banned','Perm Banned']] },
            ].map((f, fi) => (
              <select key={fi} value={f.state}
                onChange={e => f.setter(e.target.value)}
                style={{
                  padding: '9px 12px', border: '1.5px solid #E2E8F0',
                  borderRadius: 10, fontSize: 13, cursor: 'pointer',
                  background: '#fff', outline: 'none',
                }}>
                {f.opts.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            ))}
            <button onClick={() => { loadUsers(); loadAppeals(); }} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 14px', borderRadius: 10,
              border: '1.5px solid #E2E8F0', background: '#fff',
              fontSize: 13, cursor: 'pointer', color: '#64748B',
            }}>
              <RefreshCw size={13} />Refresh
            </button>
            <span style={{ fontSize: 13, color: '#94A3B8', marginLeft: 'auto' }}>
              {filtered.length} users
            </span>
          </div>

          {/* Table */}
          <div style={{
            background: '#fff', borderRadius: 14,
            border: '1px solid #E2E8F0', overflow: 'hidden',
          }}>
            {loading ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
                Loading users...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
                No users found. Check that SQL was run and admin role is set.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                  <thead>
                    <tr style={{ background: '#F8FAFF' }}>
                      {['User', 'Role', 'Institution', 'IP / Location', 'Last Seen', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{
                          padding: '12px 16px', textAlign: 'left',
                          fontSize: 11, fontWeight: 700, color: '#94A3B8',
                          letterSpacing: 0.5, borderBottom: '1px solid #F1F5F9',
                        }}>{h.toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => {
                      const role = ROLE_CONFIG[u.role] || ROLE_CONFIG.student;
                      const status = STATUS_CONFIG[u.account_status || 'active'] || STATUS_CONFIG.active;
                      const isBanned = ['temp_banned', 'permanent_banned'].includes(u.account_status);
                      return (
                        <tr key={u.id} style={{ borderBottom: '1px solid #F1F5F9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFF'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0,
                              }}>
                                {u.full_name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>
                                  {u.full_name || 'Unknown'}
                                </div>
                                <div style={{ fontSize: 11, color: '#94A3B8' }}>
                                  {u.email || `@${u.username || 'no-email'}`}
                                </div>
                                {u.warning_count > 0 && (
                                  <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 700 }}>
                                    ⚠️ {u.warning_count} warning{u.warning_count > 1 ? 's' : ''}
                                  </div>
                                )}
                                {u.delete_requested && (
                                  <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 700 }}>
                                    🗑️ Delete requested
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: '3px 8px',
                              borderRadius: 5, background: role.bg, color: role.color,
                            }}>{role.label}</span>
                            {u.degree && (
                              <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 3 }}>
                                {u.degree}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ fontSize: 13, color: '#1E293B', fontWeight: 500 }}>
                              {u.institution_name || '—'}
                            </div>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            {u.last_ip ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  fontSize: 12, color: '#475569', fontWeight: 600,
                                }}>
                                  <Globe size={11} color="#4F8EF7" />
                                  {u.last_ip}
                                </div>
                                {u.last_country && (
                                  <div style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    fontSize: 11, color: '#94A3B8',
                                  }}>
                                    <MapPin size={10} />
                                    {u.last_location || u.last_country}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, color: '#CBD5E1' }}>
                                Not recorded yet
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ fontSize: 12, color: '#64748B' }}>
                              {u.last_seen_at
                                ? new Date(u.last_seen_at).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric' })
                                : '—'}
                            </div>
                            <div style={{ fontSize: 10, color: '#94A3B8' }}>
                              Joined {new Date(u.created_at).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: '4px 8px',
                              borderRadius: 6, background: status.bg, color: status.color,
                              display: 'inline-block',
                            }}>
                              {status.label}
                            </span>
                            {u.account_status === 'temp_banned' && u.ban_until && (
                              <div style={{
                                fontSize: 10, color: '#EF4444', marginTop: 3,
                                display: 'flex', alignItems: 'center', gap: 3,
                              }}>
                                <Clock size={9} />
                                Until {new Date(u.ban_until).toLocaleDateString()}
                              </div>
                            )}
                            {u.ban_reason && (
                              <div style={{
                                fontSize: 10, color: '#94A3B8', marginTop: 2,
                                maxWidth: 120, overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {u.ban_reason}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => { setSelected(u); setActionModal('detail'); }}
                                style={{
                                  padding: '6px 10px', borderRadius: 7,
                                  border: '1px solid #E2E8F0', background: '#F8FAFF',
                                  color: '#64748B', fontSize: 11, cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                }}>
                                <Eye size={11} />View
                              </button>
                              {isBanned ? (
                                <button onClick={() => liftBan(u.id, u.full_name)}
                                  disabled={processing}
                                  style={{
                                    padding: '6px 10px', borderRadius: 7,
                                    border: '1px solid #BBF7D0', background: '#F0FDF4',
                                    color: '#16A34A', fontSize: 11, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 4,
                                  }}>
                                  <CheckCircle size={11} />Unban
                                </button>
                              ) : (
                                <button onClick={() => { setSelected(u); setActionModal('action'); }}
                                  style={{
                                    padding: '6px 10px', borderRadius: 7,
                                    border: '1px solid #FECDD3', background: '#FFF1F2',
                                    color: '#E11D48', fontSize: 11, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 4,
                                  }}>
                                  <Shield size={11} />Action
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Appeals Tab ── */}
      {activeTab === 'appeals' && (
        <div style={{
          background: '#fff', borderRadius: 14,
          border: '1px solid #E2E8F0', overflow: 'hidden',
        }}>
          {appeals.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
              <MessageSquare size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: '#64748B', marginBottom: 4 }}>
                No appeals yet
              </div>
              <div style={{ fontSize: 13 }}>Appeals submitted by users will appear here</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFF' }}>
                  {['User', 'Ban Type', 'Appeal', 'Submitted', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: '#94A3B8',
                      letterSpacing: 0.5, borderBottom: '1px solid #F1F5F9',
                    }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appeals.map(a => {
                  const ac = APPEAL_CONFIG[a.status] || APPEAL_CONFIG.pending;
                  return (
                    <tr key={a.id} style={{ borderBottom: '1px solid #F1F5F9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFF'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>
                          {a.user_name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{a.user_email}</div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 8px',
                          borderRadius: 5,
                          background: a.ban_type === 'permanent' ? '#FCA5A5' : '#FEF3C7',
                          color: a.ban_type === 'permanent' ? '#7F1D1D' : '#92400E',
                        }}>
                          {a.ban_type === 'permanent' ? '🚫 Permanent' : '⏳ Temp Ban'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', maxWidth: 250 }}>
                        <div style={{
                          fontSize: 12, color: '#475569', lineHeight: 1.5,
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {a.appeal_text}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, color: '#64748B' }}>
                          {new Date(a.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '4px 8px',
                          borderRadius: 6, background: ac.bg, color: ac.color,
                        }}>
                          {ac.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {a.status === 'pending' && (
                          <button onClick={() => { setSelectedAppeal(a); setAppealResponse(''); }}
                            style={{
                              padding: '6px 12px', borderRadius: 7,
                              border: '1px solid #BFDBFE', background: '#EFF6FF',
                              color: '#1D4ED8', fontSize: 11, cursor: 'pointer',
                              fontWeight: 600,
                            }}>
                            Review
                          </button>
                        )}
                        {a.status !== 'pending' && a.admin_response && (
                          <div style={{
                            fontSize: 11, color: '#94A3B8', maxWidth: 120,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {a.admin_response}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Detail Modal ── */}
      {actionModal === 'detail' && selected && (
        <Modal onClose={() => setActionModal(null)}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 20,
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1E293B' }}>User Details</h2>
            <button onClick={() => setActionModal(null)} style={{
              border: 'none', background: 'none', fontSize: 20,
              cursor: 'pointer', color: '#94A3B8',
            }}>×</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 900, fontSize: 20,
            }}>
              {selected.full_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17, color: '#1E293B' }}>
                {selected.full_name}
              </div>
              <div style={{ fontSize: 13, color: '#94A3B8' }}>
                {selected.email || `@${selected.username}`}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              ['Role', selected.role],
              ['Degree', selected.degree || '—'],
              ['Institution', selected.institution_name || '—'],
              ['Status', selected.account_status || 'active'],
              ['IP Address', selected.last_ip || 'Not recorded yet'],
              ['Location', selected.last_location || selected.last_country || '—'],
              ['Last Seen', selected.last_seen_at
                ? new Date(selected.last_seen_at).toLocaleString() : '—'],
              ['Joined', new Date(selected.created_at).toLocaleDateString()],
              ['Warnings', `${selected.warning_count || 0}`],
              ['Ban Until', selected.ban_until
                ? new Date(selected.ban_until).toLocaleString() : '—'],
            ].map(([k, v]) => (
              <div key={k} style={{
                background: '#F8FAFF', borderRadius: 10,
                padding: '10px 14px', border: '1px solid #E2E8F0',
              }}>
                <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 700, marginBottom: 3 }}>
                  {k.toUpperCase()}
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: '#1E293B',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{v}</div>
              </div>
            ))}
          </div>
          {selected.ban_reason && (
            <div style={{
              marginTop: 14, background: '#FFF1F2',
              border: '1px solid #FECDD3', borderRadius: 10, padding: '12px 16px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#E11D48', marginBottom: 4 }}>
                BAN / WARNING REASON
              </div>
              <div style={{ fontSize: 13, color: '#9F1239' }}>{selected.ban_reason}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setActionModal(null)} style={{
              flex: 1, padding: '12px 0', borderRadius: 10,
              border: '1px solid #E2E8F0', background: '#fff',
              color: '#64748B', fontWeight: 600, cursor: 'pointer',
            }}>Close</button>
            {['temp_banned', 'permanent_banned'].includes(selected.account_status) ? (
              <button onClick={() => liftBan(selected.id, selected.full_name)}
                disabled={processing} style={{
                  flex: 1, padding: '12px 0', borderRadius: 10, border: 'none',
                  background: '#10B981', color: '#fff', fontWeight: 700, cursor: 'pointer',
                }}>
                {processing ? 'Processing...' : 'Lift Ban'}
              </button>
            ) : (
              <button onClick={() => setActionModal('action')} style={{
                flex: 1, padding: '12px 0', borderRadius: 10, border: 'none',
                background: '#EF4444', color: '#fff', fontWeight: 700, cursor: 'pointer',
              }}>
                Take Action
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* ── Action Modal ── */}
      {actionModal === 'action' && selected && (
        <Modal onClose={() => setActionModal(null)}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 20,
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1E293B' }}>
              Take Action on {selected.full_name}
            </h2>
            <button onClick={() => setActionModal(null)} style={{
              border: 'none', background: 'none', fontSize: 20,
              cursor: 'pointer', color: '#94A3B8',
            }}>×</button>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block', fontSize: 12, fontWeight: 700,
              color: '#64748B', marginBottom: 8,
            }}>ACTION TYPE</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { value: 'warning', label: '⚠️ Warning', color: '#F59E0B' },
                { value: 'temp_ban', label: '⏳ Temp Ban', color: '#EF4444' },
                { value: 'permanent_ban', label: '🚫 Perm Ban', color: '#7F1D1D' },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => setActionForm(f => ({ ...f, type: opt.value }))}
                  style={{
                    flex: 1, padding: '10px 6px', borderRadius: 10,
                    border: actionForm.type === opt.value
                      ? `2px solid ${opt.color}` : '1.5px solid #E2E8F0',
                    background: actionForm.type === opt.value ? `${opt.color}15` : '#fff',
                    color: actionForm.type === opt.value ? opt.color : '#64748B',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {actionForm.type === 'temp_ban' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 700,
                color: '#64748B', marginBottom: 8,
              }}>BAN DURATION (DAYS)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[1, 3, 7, 14, 30].map(d => (
                  <button key={d} onClick={() => setActionForm(f => ({ ...f, days: d }))}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8,
                      border: actionForm.days === d ? '2px solid #EF4444' : '1.5px solid #E2E8F0',
                      background: actionForm.days === d ? '#FFF1F2' : '#fff',
                      color: actionForm.days === d ? '#EF4444' : '#64748B',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}>
                    {d}d
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                Expires: {new Date(Date.now() + actionForm.days * 24 * 60 * 60 * 1000)
                  .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block', fontSize: 12, fontWeight: 700,
              color: '#64748B', marginBottom: 8,
            }}>REASON (shown to user) *</label>
            <textarea
              value={actionForm.reason}
              onChange={e => setActionForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Explain the reason. The user will see this in the app."
              rows={3}
              style={{
                width: '100%', padding: '12px 14px',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontSize: 13, resize: 'none', color: '#1E293B', outline: 'none',
              }} />
          </div>

          <div style={{
            background: '#FFF7ED', border: '1px solid #FED7AA',
            borderRadius: 10, padding: '12px 14px', marginBottom: 20,
            fontSize: 12, color: '#92400E',
          }}>
            ⚡ This will immediately affect <strong>{selected.full_name}</strong>.
            They will see this when they next open the app.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setActionModal(null)} style={{
              flex: 1, padding: '12px 0', borderRadius: 10,
              border: '1px solid #E2E8F0', background: '#fff',
              color: '#64748B', fontWeight: 600, cursor: 'pointer',
            }}>Cancel</button>
            <button onClick={issueAction}
              disabled={processing || !actionForm.reason.trim()}
              style={{
                flex: 2, padding: '12px 0', borderRadius: 10, border: 'none',
                background: actionForm.type === 'warning' ? '#F59E0B'
                  : actionForm.type === 'temp_ban' ? '#EF4444' : '#7F1D1D',
                color: '#fff', fontWeight: 700, cursor: 'pointer',
                opacity: processing || !actionForm.reason.trim() ? 0.6 : 1,
              }}>
              {processing ? 'Processing...' : `Confirm ${
                actionForm.type === 'warning' ? 'Warning'
                : actionForm.type === 'temp_ban' ? `${actionForm.days}d Ban`
                : 'Permanent Ban'}`}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Appeal Review Modal ── */}
      {selectedAppeal && (
        <Modal onClose={() => setSelectedAppeal(null)}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 20,
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1E293B' }}>Review Appeal</h2>
            <button onClick={() => setSelectedAppeal(null)} style={{
              border: 'none', background: 'none', fontSize: 20,
              cursor: 'pointer', color: '#94A3B8',
            }}>×</button>
          </div>

          <div style={{
            background: '#F8FAFF', borderRadius: 12, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', marginBottom: 8 }}>
              APPELLANT
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
              {selectedAppeal.user_name}
            </div>
            <div style={{ fontSize: 12, color: '#94A3B8' }}>{selectedAppeal.user_email}</div>
          </div>

          <div style={{
            background: '#FFF7ED', border: '1px solid #FED7AA',
            borderRadius: 12, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>
              BAN REASON (original)
            </div>
            <div style={{ fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
              {selectedAppeal.ban_reason || '—'}
            </div>
          </div>

          <div style={{
            background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: 12, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1D4ED8', marginBottom: 6 }}>
              USER'S APPEAL
            </div>
            <div style={{ fontSize: 13, color: '#1E3A5F', lineHeight: 1.6 }}>
              {selectedAppeal.appeal_text}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block', fontSize: 12, fontWeight: 700,
              color: '#64748B', marginBottom: 8,
            }}>YOUR RESPONSE (optional)</label>
            <textarea
              value={appealResponse}
              onChange={e => setAppealResponse(e.target.value)}
              placeholder="Explain your decision to the user..."
              rows={3}
              style={{
                width: '100%', padding: '12px 14px',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontSize: 13, resize: 'none', outline: 'none',
              }} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setSelectedAppeal(null)} style={{
              flex: 1, padding: '12px 0', borderRadius: 10,
              border: '1px solid #E2E8F0', background: '#fff',
              color: '#64748B', fontWeight: 600, cursor: 'pointer', fontSize: 13,
            }}>Cancel</button>
            <button
              onClick={() => resolveAppeal(selectedAppeal.id, selectedAppeal.user_id, 'denied')}
              disabled={appealProcessing} style={{
                flex: 1, padding: '12px 0', borderRadius: 10, border: 'none',
                background: '#EF4444', color: '#fff', fontWeight: 700,
                cursor: 'pointer', fontSize: 13,
                opacity: appealProcessing ? 0.6 : 1,
              }}>
              ❌ Deny
            </button>
            <button
              onClick={() => resolveAppeal(selectedAppeal.id, selectedAppeal.user_id, 'approved')}
              disabled={appealProcessing} style={{
                flex: 1, padding: '12px 0', borderRadius: 10, border: 'none',
                background: '#10B981', color: '#fff', fontWeight: 700,
                cursor: 'pointer', fontSize: 13,
                opacity: appealProcessing ? 0.6 : 1,
              }}>
              ✅ Approve + Unban
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

// ── Modal wrapper ─────────────────────────────────────
function Modal({ children, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: 32,
        maxWidth: 520, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {children}
      </div>
    </div>
  );
}